import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import Navbar from 'components/layout/NavbarNew';
import UserMenu from 'components/layout/UserMenu';
import Title from 'components/Granel/TechnicalDocumentation/Title';
import HeaderControls from 'components/Granel/TechnicalDocumentation/HeaderControls';

import FileItem from 'components/Granel/TechnicalDocumentation/FileItem';
import FileCounter from 'components/Granel/TechnicalDocumentation/FileCounter';
import {
  getDownloadTechnicalDocuments,
  getTechnicalDocuments,
} from 'services/technicalDocumentationServices';
import FakeItems from 'components/Granel/TechnicalDocumentation/FakeItems';
import { useTour } from 'hooks/useTour';
import { TableWrap } from 'pages/BankSlip/styles';
import TourBalloon from 'newComponents/NewTour/TourBallon';
import {
  Scrollbar,
  BoxPadding,
  Container,
  ItemsWrap,
  EmptyItems,
} from './styles';
import ModalContent from './ModalContent';

function TechnicalDocumentation() {
  const [selectedAll, setSelectedAll] = useState(false);
  const [siteId, setSiteId] = useState(null);
  const [itemsResponse, setItensResponse] = useState([]);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mailSuccess, setMailSuccess] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [originUnique, setOriginUnique] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [requisitions, setRequisitions] = useState(0);

  const { showTour, tourType, tourStep, setTourType, setSteps } = useTour();

  const isMobile = useMediaQuery({ maxWidth: 620 });

  useEffect(() => {
    setTourType('pasta-tecnica');
    setSteps(2);
  }, [showTour, setTourType, setSteps]);

  const handleSelectItem = useCallback(
    (idItem, isChecked) => {
      const oldItems = items;
      const itemIndex = oldItems.findIndex((old) => old.itemId === idItem);
      oldItems[itemIndex].isCheckedItem = isChecked;
      setItems([...oldItems]);
    },
    [items]
  );

  const clearSelected = useCallback(() => {
    const newItems = items.map((item) => ({
      ...item,
      isCheckedItem: false,
    }));
    setItems(newItems);
    setOriginUnique(false);
  }, [items, originUnique]);

  const handleSelectedAll = useCallback(
    (isCheck) => {
      const newItems = items.map((item) => ({
        ...item,
        isCheckedItem: isCheck,
      }));
      setItems(newItems);
    },
    [items]
  );

  useEffect(() => {
    handleSelectedAll(selectedAll);
  }, [selectedAll]);

  useEffect(() => {
    async function getSiteId() {
      const customerSiteId = await localStorage.getItem(
        '@UltragazEmpresarial:customerSiteId'
      );
      setSiteId(customerSiteId);
    }
    if (!siteId) getSiteId();
  }, [siteId]);

  const getItems = useCallback(
    async (siteId, searchValue = '') => {
      setIsLoader(true);
      setRequisitions((old) => old + 1);
      const response = await getTechnicalDocuments(siteId, searchValue);
      setRequisitions((old) => old - 1);
      if (response?.length) {
        setItensResponse(response);
        setSelectedAll(false);
      } else {
        setItensResponse([]);
        setSelectedAll(false);
      }
    },
    [
      setIsLoader,
      setRequisitions,
      setItensResponse,
      setSelectedAll,
      getTechnicalDocuments,
    ]
  );

  useEffect(() => {
    if (requisitions === 0) {
      setIsLoader(false);
    }
  }, [requisitions]);

  useEffect(() => {
    if (siteId) getItems(siteId);
  }, [siteId]);

  useEffect(() => {
    if (siteId) {
      getItems(siteId, searchValue);
    }
  }, [siteId, searchValue]);

  const selectedItems = useMemo(() => {
    return items.filter((item) => item.isCheckedItem === true).length;
  }, [items]);

  useEffect(() => {
    if (selectedItems === 0) {
      setSelectedAll(false);
    }
  }, [selectedItems]);

  const itemsTransform = useMemo(() => {
    return itemsResponse.map((item, idx) => ({
      ...item,
      isCheckedItem: false,
      itemId: idx,
    }));
  }, [itemsResponse]);

  useEffect(() => {
    if (itemsTransform?.length) {
      setItems(itemsTransform);
    } else {
      setItems([]);
    }
  }, [itemsTransform]);

  const requestDocumentForDownloadOrEmail = useCallback(
    async (deliveryMethod, emailSend = null) => {
      setIsLoading(true);
      const itensForDownload = items.filter(
        (item) => item.isCheckedItem === true
      );
      const contactIdSf = localStorage.getItem(
        '@UltragazEmpresarial:contactIdSf'
      );
      if (deliveryMethod === 'EMAIL') {
        const linksForDownload = [];
        for (let i = 0; i < itensForDownload.length; i++) {
          linksForDownload.push({
            link: itensForDownload[i].link,
            documentName: itensForDownload[i].name,
            email: deliveryMethod === 'EMAIL' && emailSend ? emailSend : null,
            contactIdSf,
          });
        }

        const responseEmail = await getDownloadTechnicalDocuments({
          customerSiteId: siteId,
          deliveryMethod,
          link: null,
          documentName: null,
          email: emailSend,
          contactIdSf,
          listLinks: linksForDownload,
        });

        if (responseEmail?.status === 204) {
          setIsLoading(false);
        }
      } else {
        const countForDownload = itensForDownload.length;
        let countSuccessFiles = 0;
        for (let i = 0; i < itensForDownload.length; i++) {
          const timeOut = setTimeout(async () => {
            const responseDownload = await getDownloadTechnicalDocuments({
              customerSiteId: siteId,
              deliveryMethod,
              link: itensForDownload[i].link,
              documentName: itensForDownload[i].name,
              email: null,
              contactIdSf,
            });
            if (responseDownload?.status || responseDownload === undefined) {
              countSuccessFiles++;
            }
            if (countSuccessFiles === countForDownload) {
              setIsLoading(false);
            }
          }, i * 2000);
        }
      }
    },
    [items, siteId]
  );

  const sendMail = useCallback(async () => {
    await requestDocumentForDownloadOrEmail('EMAIL', email);
    setMailSuccess(true);
  }, [email, requestDocumentForDownloadOrEmail]);

  const label = 'Nome do arquivo';

  return (
    <Navbar selected="documentacao">
      <UserMenu
        className="pb-2"
        breadcrump="› Documentos técnicos"
        intro={() => true}
      />
      <BoxPadding>
        <Scrollbar>
          <Container>
            <Title
              title="Documentos técnicos"
              subTitle="Documentos referentes à central de gás (projeto, vistoria, laudo, etc.)"
            />
            <TableWrap
              showTour={
                showTour && tourType === 'pasta-tecnica' && tourStep === 1
              }
            >
              <HeaderControls
                showCheckBox
                selectedAll={selectedAll}
                setSelectedAll={(value) => setSelectedAll(value)}
                checkBoxTextValue="Selecionar todos"
                dowloadButton
                emailButton
                dowloadButtonText="Baixar arquivo(s)"
                emailButtonText="Enviar por e-mail"
                downloadAction={() =>
                  requestDocumentForDownloadOrEmail('DOWNLOAD')}
                emailAction={() => setShowModal(true)}
                isLoading={isLoading}
                selectedItemsNumber={selectedItems}
                hasInpuSearch
                setSearchValue={setSearchValue}
                searchValue={searchValue}
              />
              {showTour && tourType === 'pasta-tecnica' && tourStep === 1 && (
                <TourBalloon
                  title="Pesquisa"
                  content="Aqui é possível buscar pelo nome do documento. Você pode selecionar um ou mais documento(s), baixá-los ou enviá-los por e-mail."
                  position={{
                    left: '0',
                    right: '0',
                    top: '95%',
                    arrowPosition: 'top',
                    maxWidth: 300,
                    boxShadow: true,
                  }}
                />
              )}
            </TableWrap>
            {selectedItems > 0 && (
              <FileCounter fileSelectedQuantity={selectedItems} />
            )}
            <TableWrap
              showTour={
                showTour && tourType === 'pasta-tecnica' && tourStep === 2
              }
            >
              <ItemsWrap>
                {!isLoader && items.length === 0 && (
                  <EmptyItems>Nenhum documento encontrado!!</EmptyItems>
                )}
                {!isLoader &&
                  items.length > 0 &&
                  items.map((item) => (
                    <FileItem
                      label={label}
                      title={item.name}
                      isCheckedItem={item.isCheckedItem}
                      itemId={item.itemId}
                      key={item.itemId}
                      handleSelectItem={handleSelectItem}
                      link={item.link}
                      siteId={siteId}
                      description={item.details}
                      setShowModal={setShowModal}
                      noAction
                      setOriginUnique={setOriginUnique}
                      originUnique={originUnique}
                      clearSelected={clearSelected}
                    />
                  ))}
                {isLoader && <FakeItems isMobile={isMobile} />}
              </ItemsWrap>
              {showTour && tourType === 'pasta-tecnica' && tourStep === 2 && (
                <TourBalloon
                  title="Lista com os documentos"
                  content="Aqui são exibidos todos documentos referentes à central de gás (projeto, vistoria, laudo, etc.). Você pode baixar ou enviar por e-mail."
                  position={{
                    left: '50%',
                    right: '0',
                    top: '-198px',
                    arrowPosition: 'bottom',
                    maxWidth: 'unset',
                    boxShadow: false,
                    width: '492px',
                    transform: 'translateX(-50%)',
                  }}
                />
              )}
            </TableWrap>
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              dialogClassName="modal-90w"
              size="md"
              aria-labelledby="example-custom-modal-styling-title"
              animation={false}
              centered
            >
              <ModalContent
                setMailSuccess={setMailSuccess}
                mailSuccess={mailSuccess}
                sendMail={sendMail}
                isLoading={isLoading}
                email={email}
                setEmail={setEmail}
                close={() => setShowModal(false)}
                originUnique={originUnique}
                clearSelected={clearSelected}
              />
            </Modal>
          </Container>
        </Scrollbar>
      </BoxPadding>
    </Navbar>
  );
}

export default TechnicalDocumentation;
